import React, { useEffect, useState } from "react";
import { useLocation, Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { useEnquiryModal } from "./EnquiryModal";
import { GoDotFill } from "react-icons/go";
import { ChevronLeft, ChevronRight } from "lucide-react";
import Footer from "./Footer";
import FooterMain from "./FooterMain";

export default function CardPage() {
    const { state: property } = useLocation();
    const { openModal } = useEnquiryModal();
    const [current, setCurrent] = useState(0);
    const [isMobile, setIsMobile] = useState(false);

    const images = [
        property.image,
        property.image1,
        property.image2,
        property.image3,
        property.image4,
    ].filter(Boolean);

    const galleryImages = [
        property.image,
        property.image1,
        property.image2,
        property.image3,
        property.image4,
    ];

    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);
    useEffect(() => {
        const handleResize = () => {
            setIsMobile(window.innerWidth < 768);
        };
        handleResize();
        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener("resize", handleResize);
    }, []);

    useEffect(() => {
        const interval = setInterval(() => {
            setCurrent((prev) => (prev + 1) % images.length);
        }, 4000);
        return () => clearInterval(interval);
    }, []);

    const prevSlide = () => {
        setCurrent((prev) => (prev === 0 ? images.length - 1 : prev - 1));
    };

    const nextSlide = () => {
        setCurrent((prev) => (prev + 1) % images.length);
    };

    if (!property) {
        return (
            <div className="p-10 text-center text-xl text-gray-600">
                Property details not found. Please go back to the listing page.
            </div>
        );
    }

    return (
        <>
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-20 py-6 sm:py-10">
                <div className="container justify-between items-center">
                    {/* Breadcrumb */}
                    <div className="flex space-x-2 text-[#000000]">
                        <Link to="/" className="hover:underline text-xl">
                            Home
                        </Link>
                        <GoDotFill size={10} className="mt-2.5 text-[#000000]" />
                        <Link to="/" className="hover:underline text-[#000000] text-xl">
                            Property
                        </Link>
                        <GoDotFill size={10} className="mt-2.5 text-[#000000]" />
                        <span className="text-[#000000] font-medium text-xl">{property.location}</span>
                    </div>

                    {/* Main Section */}
                    <div key={property.id} id="gallery" className="w-full mt-4">
                        <div className="container mx-auto grid grid-cols-1 md:grid-cols-2 items-center gap-6 px-4">
                            {/* Text Section */}
                            <div className="space-y-4 text-center md:text-left md:order-1 order-2">
                                <h2 className="luxury-text text-2xl md:text-5xl font-semibold text-[#000000]">{property.title}</h2>
                                <h2 className="luxury-text text-2xl md:text-4xl font-semibold text-[#000000]">At {property.location}</h2>
                                <div className="flex md:flex-row flex-col gap-4">
                                    <button onClick={(e) => {
                                            e.stopPropagation(); // card click prevent
                                            openModal({
                                            projectTitle: property.project_name,   // THIS IS IMPORTANT
                                            projectId: property.id,
                                            });
                                        }} 
                                        className="font-semibold border-2 rounded-md border-sky-600 bg-sky-600 text-[#ffffff] hover:text-sky-600 px-6 py-2 hover:bg-[#ffffff] transition">
                                        Download Brochure
                                    </button>
                                    <button onClick={(e) => {
                                            e.stopPropagation(); // card click prevent
                                            openModal({
                                            projectTitle: property.project_name,   // THIS IS IMPORTANT
                                            projectId: property.id,
                                            });
                                        }}
                                        className="font-semibold border-2 rounded-md border-sky-600 text-sky-600 hover:text-white px-6 py-2 hover:bg-sky-600 transition">
                                        Get a Consultation
                                    </button>
                                </div>
                            </div>

                            {/* Carousel Section */}
                            <div className="relative w-full overflow-hidden rounded-2xl shadow-lg md:order-2 order-1">
                                <AnimatePresence mode="wait">
                                    <motion.img
                                        key={current}
                                        src={images[current]}
                                        alt="gallery"
                                        className="w-full h-64 md:h-[300px] object-cover"
                                        initial={{ opacity: 0, x: 100 }}
                                        animate={{ opacity: 1, x: 0 }}
                                        exit={{ opacity: 0, x: -100 }}
                                        transition={{ duration: 0.5 }}
                                    />
                                </AnimatePresence>

                                {/* Arrows only in mobile view */}
                                {isMobile && (
                                    <>
                                        <button
                                            onClick={prevSlide}
                                            className="absolute top-1/2 left-3 -translate-y-1/2 bg-white/70 p-2 rounded-full shadow-md"
                                        >
                                            <ChevronLeft className="w-5 h-5" />
                                        </button>
                                        <button
                                            onClick={nextSlide}
                                            className="absolute top-1/2 right-3 -translate-y-1/2 bg-white/70 p-2 rounded-full shadow-md"
                                        >
                                            <ChevronRight className="w-5 h-5" />
                                        </button>
                                    </>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Row Section */}
                    <div className="w-full grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mt-4">
                        <div className="bg-gray-200 p-4 rounded-lg shadow-lg hover:shadow-xl">
                            <h2 className="text-lg md:text-xl font-semibold">{property.price}</h2>
                            <h2 className="text-md">Starting Price</h2>
                        </div>
                        <div className="bg-gray-200 p-4 rounded-lg shadow-lg hover:shadow-xl">
                            <h2 className="text-lg md:text-xl font-semibold">{property.bed}</h2>
                            <h2 className="text-md">Number of Bedrooms</h2>
                        </div>
                        <div className="bg-gray-200 p-4 rounded-lg shadow-lg hover:shadow-xl">
                            <h2 className="text-lg md:text-xl font-semibold">{property.area}</h2>
                            <h2 className="text-md">Area Range</h2>
                        </div>
                        <div className="bg-gray-200 p-4 rounded-lg shadow-lg hover:shadow-xl">
                            <h2 className="text-lg md:text-xl font-semibold">{property.location}</h2>
                            <h2 className="text-md">Location</h2>
                        </div>
                    </div>

                    {/* About section */}
                    <div className="w-full items-center grid grid-cols-1 md:grid-cols-2 gap-6 mt-10">
                        <div className="">
                            <img src={property.image1} alt="DL1" className="rounded-xl hover:shadow-lg h-full w-full object-cover" />
                        </div>
                        <div className="text-md text-black">
                            <h2 className="text-2xl md:text-4xl font-semibold text-[#000000] mb-2">About {property.title}</h2>
                            <p className="">{property.description}</p>
                        </div>
                    </div>

                    {/* Payment Plan */}
                    <section className="bg-white mt-10">
                        {/* Master Plan Section */}
                        {/* <div className="text-center mb-10">
                            <h2 className="text-2xl md:text-3xl font-serif text-[#000000] tracking-wide mb-4">
                                MASTER PLAN LAYOUT
                            </h2>
                            <button onClick={openModal} className=" rounded-md border-2 border-[#003153] font-semibold text-[#003153] px-6 py-2 text-sm hover:bg-[#003153] hover:text-white transition">
                                REQUEST MASTER PLAN LAYOUT
                            </button>
                        </div> */}

                        {/* Image Grid */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
                            {/* Left Image */}
                            <div className="relative rounded-lg group shadow-md overflow-hidden">
                                <img
                                    src={property.masterPlanImage1}
                                    alt="Master Plan"
                                    className="w-full h-64 md:h-80 object-cover transform"
                                />
                                <button onClick={openModal} className="absolute inset-0 flex items-center justify-center bg-black/40 opacity-100 text-white font-medium text-sm transition">
                                    REQUEST MASTER PLAN LAYOUT
                                </button>
                            </div>

                            {/* Right Image */}
                            <div className="relative rounded-lg group shadow-md overflow-hidden">
                                <img
                                    src={property.masterPlanImage2}
                                    alt="Unit Plan"
                                    className="w-full h-64 md:h-80 object-cover transform"
                                />
                                <button onClick={openModal} className="absolute inset-0 flex items-center justify-center bg-black/40 opacity-100 text-white font-medium text-sm transition">
                                    REQUEST UNIT PLAN LAYOUT
                                </button>
                            </div>
                        </div>

                        {/* Unit Plan Section */}
                        {/* <div className="text-center mt-12">
                            <h2 className="text-2xl md:text-3xl font-serif text-[#000000] tracking-wide mb-4">
                                UNIT PLAN LAYOUT
                            </h2>
                            <button onClick={openModal} className="border-2 font-semibold rounded-md border-[#003153] text-[#003153] px-6 py-2 text-sm hover:bg-[#003153] hover:text-white transition">
                                REQUEST UNIT PLAN LAYOUT
                            </button>
                        </div> */}
                    </section>

                    {/* Gallery Section */}
                    <div className="w-full mt-10">
                        {/* <h2 className="text-2xl md:text-4xl font-semibold text-[#000000] mb-6">Gallery</h2> */}
                        <div className="grid grid-cols-1 md:grid-cols-2 items-center gap-6">
                            <div className="relative w-full overflow-hidden rounded-2xl shadow-lg md:order-2 order-1">
                                <AnimatePresence mode="wait">
                                    <motion.img
                                        key={current}
                                        src={galleryImages[current]}
                                        alt="gallery"
                                        className="w-full h-64 md:h-[300px] object-cover"
                                        initial={{ opacity: 0, x: 100 }}
                                        animate={{ opacity: 1, x: 0 }}
                                        exit={{ opacity: 0, x: -100 }}
                                        transition={{ duration: 0.5 }}
                                    />
                                </AnimatePresence>

                                {/* Arrows only in mobile view */}
                                {isMobile && (
                                    <>
                                        <button
                                            onClick={prevSlide}
                                            className="absolute top-1/2 left-3 -translate-y-1/2 bg-white/70 p-2 rounded-full shadow-md"
                                        >
                                            <ChevronLeft className="w-5 h-5" />
                                        </button>
                                        <button
                                            onClick={nextSlide}
                                            className="absolute top-1/2 right-3 -translate-y-1/2 bg-white/70 p-2 rounded-full shadow-md"
                                        >
                                            <ChevronRight className="w-5 h-5" />
                                        </button>
                                    </>
                                )}
                            </div>
                            <div className="bg-gray-200 p-4 rounded-xl shadow-lg hover:shadow-xl">
                                <h2 className="text-xl md:text-4xl font-semibold text-[#000000] mb-4">Project Details</h2>
                                <div className="md:text-lg text-md flex justify-between mb-2">
                                    <h2 className="text-[#000000]">Starting Price</h2>
                                    <h2 className="font-semibold text-[#000000]">{property.price}</h2>
                                </div>
                                <div className="md:text-lg text-md flex justify-between mb-2">
                                    <h2 className="text-[#000000]">Property Type</h2>
                                    <h2 className="font-semibold text-[#000000]">{property.type}</h2>
                                </div>
                                <div className="md:text-lg text-md flex justify-between mb-2">
                                    <h2 className="text-[#000000]">Number of Bedrooms</h2>
                                    <h2 className="font-semibold text-[#000000]">{property.bed}</h2>
                                </div>
                                <div className="md:text-lg text-md flex justify-between mb-2">
                                    <h2 className="text-[#000000]">Area Range</h2>
                                    <h2 className="font-semibold text-[#000000]">{property.area}</h2>
                                </div>
                                <div className="md:text-lg text-md flex justify-between mb-2">
                                    <h2 className="text-[#000000]">Location</h2>
                                    <h2 className="font-semibold text-[#000000]">{property.location}</h2>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Location & Payment Plan section */}
                    <div className="w-full grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mt-10">
                        <div className="col-span-2 space-y-4 bg-gray-200 p-6 rounded-lg shadow-lg hover:shadow-xl">
                            <h2 className="text-2xl md:text-4xl font-semibold text-[#000000]">Location</h2>
                            <div className="bg-white p-4 rounded-lg shadow-md text-[#000000] shadow-lg hover:shadow-xl">
                                <h2 className="text-xl md:text-2xl font-semibold">5-10 Minutes</h2>
                                <p className="text-md mt-2">{property.locationDescription1}</p>
                            </div>
                            <div className="bg-white p-4 rounded-lg shadow-md text-[#000000] shadow-lg hover:shadow-xl">
                                <h2 className="text-xl md:text-2xl font-semibold">15-20 Minutes</h2>
                                <p className="text-md mt-2">{property.locationDescription2}</p>
                            </div>
                            <div className="bg-white p-4 rounded-lg shadow-md text-[#000000] shadow-lg hover:shadow-xl">
                                <h2 className="text-xl md:text-2xl font-semibold">20 Minutes & More</h2>
                                <p className="text-md mt-2">{property.locationDescription3}</p>
                            </div>
                        </div>
                        <div className="bg-white p-6 space-y-4 rounded-lg">
                            <h2 className="text-2xl md:text-4xl font-semibold text-[#000000]">Payment Plan</h2>
                            {/* <div className="bg-gray-200 p-4 rounded-lg shadow-md text-[#000000] shadow-lg hover:shadow-xl">
                                <h2 className="text-xl md:text-2xl font-semibold">{property.downPayment}</h2>
                                <p className="text-md mt-2">Down Payment at Booking</p>
                            </div>
                            <div className="bg-gray-200 p-4 rounded-lg shadow-md text-[#000000] shadow-lg hover:shadow-xl">
                                <h2 className="text-xl md:text-2xl font-semibold">{property.onConstruction}</h2>
                                <p className="text-md mt-2">On Construction</p>
                            </div>
                            <div className="bg-gray-200 p-4 rounded-lg shadow-md text-[#000000] shadow-lg hover:shadow-xl">
                                <h2 className="text-xl md:text-2xl font-semibold">{property.onHandover}</h2>
                                <p className="text-md mt-2">On Handover</p>
                            </div> */}
                            {property.paymentPlan?.map((plan, index) => (
                                <div
                                    key={index}
                                    className="bg-gray-200 p-4 rounded-lg shadow-md text-[#000000] hover:shadow-xl"
                                >
                                    <h2 className="text-xl md:text-2xl font-semibold">
                                        {plan.value}
                                    </h2>
                                    <p className="text-md mt-2">{plan.label}</p>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </div>
            <Footer />
            <FooterMain />
        </>
    );
}
